import { supabase } from '@/lib/supabase';
import type {
  ConversationStatus,
  LeadConversationsInsert,
  LeadConversationsRow,
  LeadConversationsUpdate,
  LeadMessagesInsert,
  LeadMessagesRow,
  ProspectNotesRow,
} from '@/types/database.types';

export interface LeadsFilters {
  search?: string;
  status?: ConversationStatus;
}

export interface LeadsListResult {
  rows: LeadConversationsRow[];
  count: number;
}

const PAGE_SIZE = 25;

export async function listConversations(
  filters: LeadsFilters,
  page: number,
): Promise<LeadsListResult> {
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;
  let q = supabase
    .from('lead_conversations')
    .select('*', { count: 'exact' })
    .order('updated_at', { ascending: false })
    .range(from, to);
  if (filters.status) q = q.eq('status', filters.status);
  if (filters.search) q = q.ilike('subject', `%${filters.search}%`);
  const { data, error, count } = await q;
  if (error) throw error;
  return { rows: (data ?? []) as LeadConversationsRow[], count: count ?? 0 };
}

export async function getConversation(
  id: string,
): Promise<LeadConversationsRow | null> {
  const { data, error } = await supabase
    .from('lead_conversations')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as LeadConversationsRow | null) ?? null;
}

export async function listMessages(
  conversationId: string,
): Promise<LeadMessagesRow[]> {
  const { data, error } = await supabase
    .from('lead_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('occurred_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as LeadMessagesRow[];
}

export async function createConversation(
  input: LeadConversationsInsert,
): Promise<LeadConversationsRow> {
  const { data, error } = await supabase
    .from('lead_conversations')
    .insert(input)
    .select('*')
    .single();
  if (error) throw error;
  return data as LeadConversationsRow;
}

export async function updateConversation(
  id: string,
  patch: LeadConversationsUpdate,
): Promise<LeadConversationsRow> {
  const { data, error } = await supabase
    .from('lead_conversations')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as LeadConversationsRow;
}

export async function createMessage(
  input: LeadMessagesInsert,
): Promise<LeadMessagesRow> {
  const { data, error } = await supabase
    .from('lead_messages')
    .insert(input)
    .select('*')
    .single();
  if (error) throw error;
  return data as LeadMessagesRow;
}

export interface OriginCampaignEmail {
  id: string;
  subject: string;
  body: string;
  sent_at: string | null;
  from_address: string;
  to_address: string;
}

/**
 * El mail de campaña que conectó y generó el lead (el que el prospecto
 * respondió). Abre el timeline de la conversación como primer outbound.
 */
export async function getOriginCampaignEmail(
  conversationId: string,
): Promise<OriginCampaignEmail | null> {
  const { data: firstInbound } = await supabase
    .from('lead_messages')
    .select('campaign_email_id')
    .eq('conversation_id', conversationId)
    .not('campaign_email_id', 'is', null)
    .order('occurred_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  const ceId = firstInbound?.campaign_email_id;
  if (!ceId) return null;
  const { data, error } = await supabase
    .from('campaign_emails')
    .select('id, subject, body, sent_at, from_address, to_address')
    .eq('id', ceId)
    .maybeSingle();
  if (error) throw error;
  return (data as OriginCampaignEmail | null) ?? null;
}

/** Marca la conversación como leída (no-leído = last_inbound_at > last_read_at). */
export async function markConversationRead(id: string): Promise<void> {
  const { error } = await supabase
    .from('lead_conversations')
    .update({ last_read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export interface UnreadConversation {
  id: string;
  subject: string | null;
  last_inbound_at: string;
}

/**
 * Conversaciones con respuestas sin leer del user actual (RLS filtra a las
 * propias). PostgREST no compara columna-vs-columna, así que se computa acá
 * — el volumen de conversaciones por user es chico.
 */
export async function listUnreadConversations(): Promise<UnreadConversation[]> {
  const { data, error } = await supabase
    .from('lead_conversations')
    .select('id, subject, last_inbound_at, last_read_at')
    .not('last_inbound_at', 'is', null)
    .order('last_inbound_at', { ascending: false });
  if (error) throw error;
  return (data ?? [])
    .filter((c) => {
      const inbound = c.last_inbound_at ? Date.parse(c.last_inbound_at) : 0;
      const read = c.last_read_at ? Date.parse(c.last_read_at) : 0;
      return inbound > read;
    })
    .map((c) => ({
      id: c.id,
      subject: c.subject,
      last_inbound_at: c.last_inbound_at as string,
    }));
}

/**
 * Notas internas del lead: viven en core.prospect_notes con
 * metadata.conversation_id (visibles también en la ficha del prospecto).
 */
export async function listLeadNotes(
  conversationId: string,
): Promise<ProspectNotesRow[]> {
  const { data, error } = await supabase
    .from('prospect_notes')
    .select('*')
    .contains('metadata', { conversation_id: conversationId })
    .is('deleted_at', null)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ProspectNotesRow[];
}

export async function createLeadNote(input: {
  conversationId: string;
  prospectId: string;
  authorUserId: string;
  body: string;
}): Promise<ProspectNotesRow> {
  const { data, error } = await supabase
    .from('prospect_notes')
    .insert({
      prospect_id: input.prospectId,
      author_user_id: input.authorUserId,
      kind: 'note',
      body: input.body,
      source: 'lead_chat',
      metadata: { conversation_id: input.conversationId },
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as ProspectNotesRow;
}

export async function countByStatus(): Promise<
  Partial<Record<ConversationStatus, number>>
> {
  const { data, error } = await supabase
    .from('lead_conversations')
    .select('status');
  if (error) throw error;
  const out: Partial<Record<ConversationStatus, number>> = {};
  for (const row of data ?? []) {
    const s = (row as { status: ConversationStatus }).status;
    out[s] = (out[s] ?? 0) + 1;
  }
  return out;
}
